<template>
  <div>
    <Head :title="__(title)" />
    <div class="main flex-1 flex flex-col sec_chat">
      <div class="flex-1 flex h-full gap-5">
        <!-- Sidebar -->
        <div class="sidebar h-full hidden lg:flex w-1/3 flex-2 flex-col bg-white">
          <div class="chat-box h-full">
            <div class="chat-left-aside h-full">
              <div class="c-top">
                <div class="media mb-1">
                  <img
                    v-if="user.photo" :src="user.photo" alt=""
                    class="rounded-circle rounded-full user-image"
                  />
                  <img
                    v-else src="/images/svg/profile.svg" alt=""
                    class="rounded-circle rounded-full user-image"
                  />
                  <div class="about">
                    <div class="name f-w-600">{{ user.first_name }} {{ user.last_name }}</div>
                    <div class="status">{{ __(user.role.name) }}</div>
                  </div>
                </div>
                <div class="search flex-2 py-2">
                  <search-input v-model="form.search" class="w-full" @reset="reset" />
                </div>
                <!-- WhatsApp toggle button -->
                <div class="flex justify-center mb-2">
                  <button
                    class="px-4 py-2 bg-green-500 text-white rounded-md"
                    @click="toggleWhatsAppView"
                  >
                    {{ showWhatsApp ? __('Show Regular Chats') : __('Show WhatsApp Chats') }}
                  </button>
                </div>
              </div>
              <div class="c-list">
                <div class="flex-1 h-full overflow-hidden overflow-y-auto pt-2">
                  <!-- Regular Conversations -->
                  <template v-if="!showWhatsApp">
                    <Link
                      v-for="conversation in conversations.data" :key="conversation.id"
                      :href="route('chat.current', conversation.slug || conversation.id)"
                      class="entry cursor-pointer transform hover:scale-105 duration-300 transition-transform mb-4 rounded p-4 mx-5 flex shadow-md"
                      :class="{ 'border-l-4 border-red-500': chat && (conversation.id === chat.id) }"
                    >
                      <div class="flex-2">
                        <div class="w-12 h-12 relative">
                          <span
                            class="w-12 h-12 rounded-full mx-auto user_icon"
                            alt="chat-user"
                          >{{ conversation.creator?.first_name ?
                            Array.from(conversation.creator.first_name)[0] : '' }}</span>
                        </div>
                      </div>
                      <div class="flex-1 px-2">
                        <div class="truncate w-32">
                          <span class="">{{
                            conversation.creator?.first_name || '' }} {{
                            conversation.creator?.last_name || '' }}</span>
                        </div>
                        <div v-if="conversation.creator?.email" class="mt-1">
                          <small
                            class="text-gray-600"
                          >{{ conversation.creator.email }}</small>
                        </div>
                        <div v-if="conversation.title" class="mt-1 truncate w-32">
                          <small
                            class="text-gray-600 text-xs"
                          >{{ conversation.title }}</small>
                        </div>
                      </div>
                      <div class="flex-2 text-right">
                        <div>
                          <small class="text-gray-500">{{
                            moment(conversation.updated_at).format('DD MMM') }}</small>
                        </div>
                        <div>
                          <small
                            v-if="conversation.total_entry"
                            class="text-xs bg-red-500 text-white rounded-full h-6 w-6 leading-6 text-center inline-block"
                          >
                            {{ conversation.total_entry }}
                          </small>
                        </div>
                      </div>
                    </Link>
                  </template>

                  <!-- WhatsApp Conversations -->
                  <!-- In your WhatsApp conversations list template -->
                  <template v-else>
                    <div
                      v-for="waChat in whatsappConversations" :key="waChat.phone"
                      class="entry cursor-pointer transform hover:scale-105 duration-300 transition-transform mb-4 rounded p-4 mx-5 flex shadow-md"
                      :class="{ 'border-l-4 border-green-500': activeWhatsAppChat === waChat.phone }"
                      @click="loadWhatsAppChat(waChat.phone)"
                    >
                      <div class="flex-2">
                        <div class="w-12 h-12 relative">
                          <span
                            class="w-12 h-12 rounded-full mx-auto bg-green-100 text-green-600 flex items-center justify-center"
                          >
                            <icon name="whatsapp" class="w-6 h-6" />
                          </span>
                          <!-- Add small status indicator -->
                          <span
                            v-if="waChat.isLive"
                            class="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white"
                          />
                          <span
                            v-else
                            class="absolute bottom-0 right-0 w-3 h-3 bg-gray-400 rounded-full border-2 border-white"
                          />
                        </div>
                      </div>
                      <div class="flex-1 px-2">
                        <div class="truncate w-32">
                          <span>{{ waChat.phone }}</span>
                        </div>
                        <div v-if="waChat.lastMessage" class="mt-1 truncate w-32">
                          <small class="text-gray-600 text-xs">{{ waChat.lastMessage
                          }}</small>
                        </div>
                        <!-- Add status text -->
                        <div class="mt-1">
                          <small
                            class="text-xs"
                            :class="waChat.isLive ? 'text-green-600' : 'text-gray-500'"
                          >
                            {{ waChat.isLive ? 'Live chat active' : 'Live chat inactive' }}
                          </small>
                        </div>
                      </div>
                      <div class="flex-2 text-right">
                        <div>
                          <small class="text-gray-500">
                            {{ formatWhatsAppConversationDate(waChat) }}
                          </small>
                        </div>
                      </div>
                    </div>
                  </template>

                  <div
                    v-if="(!showWhatsApp && !conversations.data.length) || (showWhatsApp && !whatsappConversations.length)"
                    class="flex w-full h-full justify-center items-center text-gray-500"
                  >
                    {{ showWhatsApp ? __('No WhatsApp conversations found.') : __('No conversation found.') }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Chat Area -->
        <div class="chat-area h-full w-2/3 flex-1 flex flex-col bg-white">
          <div class="flex-3">
            <div v-if="chat && !showWhatsApp" class="chat-header flex pb-3 justify-between">
              <!-- Regular chat header -->
              <div class="flex">
                <div class="w-12 h-12 relative mr-3">
                  <span
                    class="w-12 h-12 rounded-full mx-auto user_icon"
                    :title="(chat?.creator?.first_name || '') + ' ' + (chat?.creator?.last_name || '')"
                  >
                    {{ chat?.creator?.first_name ? Array.from(chat.creator.first_name)[0] : '' }}
                  </span>
                </div>
                <div class="flex flex-col pt-1">
                  <div class="name">
                    {{ chat?.creator?.first_name || '' }} {{ chat?.creator?.last_name
                      || '' }}
                  </div>
                  <div v-if="chat?.creator?.email" class="status digits pt-2">
                    {{ chat.creator.email
                    }}
                  </div>
                </div>
              </div>
              <div
                v-if="user_access.chat.delete"
                class="flex justify-center mr-3 items-center cursor-pointer" @click="destroy(chat.id)"
              >
                <icon name="trash" class="w-5 h-5" />
              </div>
            </div>

            <!-- In your chat-area div, modify the WhatsApp chat header -->
            <div v-if="showWhatsApp && activeWhatsAppChat" class="chat-header flex pb-3 justify-between">
              <div class="flex">
                <div class="w-12 h-12 relative mr-3">
                  <span
                    class="w-12 h-12 rounded-full mx-auto bg-green-100 text-green-600 flex items-center justify-center"
                  >
                    <icon name="whatsapp" class="w-6 h-6" />
                  </span>
                </div>
                <!-- In your WhatsApp chat header -->
                <div class="flex flex-col pt-1">
                  <div class="name">{{ activeWhatsAppChat }}</div>
                  <div class="status digits pt-2">WhatsApp Chat</div>
                  <!-- Status indicator with last seen time -->
                  <div class="flex items-center mt-1">
                    <span class="text-xs mr-2">Status:</span>
                    <span
                      class="text-xs font-semibold"
                      :class="currentChatStatus.isLive ? 'text-green-600' : 'text-red-600'"
                    >
                      {{ currentChatStatus.isLive ? 'Active' : 'Inactive' }}
                    </span>
                    <!-- <span class="text-xs text-gray-500 ml-2" v-if="currentChatStatus.lastSeen">
                                        (Last seen: {{ moment(currentChatStatus.lastSeen).fromNow() }})
                                    </span> -->
                  </div>
                  <!-- Last replied info -->
                  <div
                    v-if="currentChatStatus.lastRepliedBy && currentChatStatus.lastRepliedOn"
                    class="text-xs text-gray-500 mt-1"
                  >
                    Last replied by {{ currentChatStatus.lastRepliedBy }}
                    {{ moment(currentChatStatus.lastRepliedOn).fromNow() }}
                  </div>
                </div>
              </div>

              <LoadingButton
                class="px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2"
                :class="{
                  'bg-red-100 text-red-700 hover:bg-red-200 focus:ring-red-500': currentChatStatus.isLive,
                  'bg-green-100 text-green-700 hover:bg-green-200 focus:ring-green-500': !currentChatStatus.isLive
                }"
                :loading="processingStatusChange" @click="toggleLiveChatStatus"
              >
                <span v-if="!processingStatusChange">
                  {{ currentChatStatus.isLive ? 'End Live Chat' : 'Start Live Chat' }}
                </span>
                <span v-else>
                  {{ currentChatStatus.isLive ? 'Ending...' : 'Starting...' }}
                </span>
              </LoadingButton>
            </div>
          </div>

          <!-- Messages Area -->
          <div class="messages flex-1 overflow-auto reverse__order">
            <!-- Regular Chat Messages -->
            <template v-if="chat && chat.messages.length && !showWhatsApp">
              <div
                v-for="(message, index) in chat.messages.slice().reverse()" :key="index"
                class="message mb-4 flex"
              >
                <div
                  v-if="!message.user_id && message.contact && message.contact.first_name"
                  class="flex-2"
                >
                  <div class="w-12 h-12 relative">
                    <span class="w-12 h-12 rounded-full mx-auto user_icon">{{
                      message.contact?.first_name ? Array.from(message.contact.first_name)[0] : ''
                    }}</span>
                  </div>
                </div>
                <div v-if="!message.user_id" class="flex-1 px-2 mt__wrap">
                  <div class="inline-block bg-gray-300 rounded p-2 px-3 text-gray-700 leading-5">
                    <span>{{ message.message }}</span>
                  </div>
                  <div class="pl-4">
                    <small class="text-gray-500">{{
                      moment(message.updated_at).fromNow(true) }}</small>
                  </div>
                </div>

                <div v-if="message.user_id" class="flex-1 px-2 text-right mt__wrap">
                  <div class="inline-block bg-blue-600 rounded p-2 px-3 text-white leading-5">
                    <span>{{ message.message }}</span>
                  </div>
                  <div class="pr-4">
                    <small class="text-gray-500">{{
                      moment(message.updated_at).fromNow(true) }}</small>
                  </div>
                </div>
              </div>
            </template>

            <!-- WhatsApp Messages -->

            <!-- WhatsApp Messages -->
            <!-- In the WhatsApp Messages template section -->
            <template v-else-if="showWhatsApp && whatsappMessages.length">
              <div
                v-for="(message, index) in whatsappMessages.slice().reverse()" :key="'wa-' + index"
                class="message mb-4 flex"
              >
                <div v-if="message.fromPhone === activeWhatsAppChat" class="flex-2">
                  <div class="w-12 h-12 relative">
                    <span
                      class="w-12 h-12 rounded-full mx-auto bg-green-100 text-green-600 flex items-center justify-center"
                    >
                      <icon name="whatsapp" class="w-6 h-6" />
                    </span>
                  </div>
                </div>
                <div v-if="message.fromPhone === activeWhatsAppChat" class="flex-1 px-2 mt__wrap">
                  <!-- Check if message contains a URL that's an image -->
                  <div
                    v-if="isImageUrl(message.messageText)"
                    class="inline-block bg-gray-300 rounded p-2 px-3 text-gray-700 leading-5"
                  >
                    <div class="max-w-xs">
                      <img
                        :src="message.messageText" class="max-h-48 rounded cursor-pointer hover:opacity-90 transition-opacity"
                        alt="WhatsApp image"
                        @click="openLightbox(message.messageText)"
                      />
                      <div class="mt-1 text-xs">
                        <a
                          :href="message.messageText"
                          class="text-blue-600 hover:underline"
                          @click.prevent="downloadFile(message.messageText, 'image')"
                        >
                          Download Image
                        </a>
                      </div>
                    </div>
                    <div class="pl-4">
                      <small class="text-gray-500">{{
                        message.pending ? __('sending...') : moment(message.sentTimeStamp).fromNow(true) }}</small>
                    </div>
                  </div>
                  <!-- Check if message contains a URL that's a file -->
                  <div
                    v-else-if="isFileUrl(message.messageText)"
                    class="inline-block bg-gray-300 rounded p-2 px-3 text-gray-700 leading-5"
                  >
                    <div class="max-w-xs">
                      <div class="flex items-center p-2 bg-gray-100 rounded">
                        <icon name="file" class="w-5 h-5 mr-2 text-gray-500" />
                        <span class="truncate">{{ getFileNameFromUrl(message.messageText)
                        }}</span>
                      </div>
                      <div class="mt-1 text-xs">
                        <a
                          :href="message.messageText"
                          class="text-blue-600 hover:underline"
                          @click.prevent="downloadFile(message.messageText, 'file')"
                        >
                          Download File
                        </a>
                      </div>
                    </div>
                    <div class="pl-4">
                      <small class="text-gray-500">{{
                        message.pending ? __('sending...') : moment(message.sentTimeStamp).fromNow(true) }}</small>
                    </div>
                  </div>
                  <!-- Regular text message -->
                  <div
                    v-else
                    class="inline-block bg-gray-300 rounded p-2 px-3 text-gray-700 leading-5"
                  >
                    <span>{{ message.messageText }}</span>
                    <div class="pl-4">
                      <small class="text-gray-500">{{
                        message.pending ? __('sending...') : moment(message.sentTimeStamp).fromNow(true) }}</small>
                    </div>
                  </div>
                </div>

                <div
                  v-if="message.toPhone === activeWhatsAppChat"
                  class="flex-1 px-2 text-right mt__wrap"
                >
                  <!-- Check if message contains a URL that's an image (sent by agent) -->
                  <div
                    v-if="isImageUrl(message.messageText)"
                    class="inline-block bg-green-600 rounded p-2 px-3 text-white leading-5"
                  >
                    <div class="max-w-xs">
                      <img
                        :src="message.messageText" class="max-h-48 rounded cursor-pointer hover:opacity-90 transition-opacity"
                        alt="WhatsApp image"
                        @click="openLightbox(message.messageText)"
                      />
                      <div class="mt-1 text-xs">
                        <a
                          :href="message.messageText"
                          class="text-blue-200 hover:underline"
                          @click.prevent="downloadFile(message.messageText, 'image')"
                        >
                          Download Image
                        </a>
                      </div>
                    </div>
                    <div class="pr-4">
                      <small class="text-gray-500">{{
                        message.pending ? __('sending...') : moment(message.sentTimeStamp).fromNow(true) }}</small>
                    </div>
                  </div>
                  <!-- Check if message contains a URL that's a file (sent by agent) -->
                  <div
                    v-else-if="isFileUrl(message.messageText)"
                    class="inline-block bg-green-600 rounded p-2 px-3 text-white leading-5"
                  >
                    <div class="max-w-xs">
                      <div class="flex items-center p-2 bg-green-700 rounded">
                        <icon name="file" class="w-5 h-5 mr-2 text-green-200" />
                        <span class="truncate">{{ getFileNameFromUrl(message.messageText)
                        }}</span>
                      </div>
                      <div class="mt-1 text-xs">
                        <a
                          :href="message.messageText"
                          class="text-blue-200 hover:underline"
                          @click.prevent="downloadFile(message.messageText, 'file')"
                        >
                          Download File
                        </a>
                      </div>
                    </div>
                    <div class="pr-4">
                      <small class="text-gray-500">{{
                        message.pending ? __('sending...') : moment(message.sentTimeStamp).fromNow(true) }}</small>
                    </div>
                  </div>
                  <!-- Regular text message (sent by agent) -->
                  <div v-else class="inline-block bg-green-600 rounded p-2 px-3 text-white leading-5">
                    <span>{{ message.messageText }}</span>
                    <div class="pr-4">
                      <small class="text-gray-500">{{
                        message.pending ? __('sending...') : moment(message.sentTimeStamp).fromNow(true) }}</small>
                    </div>
                  </div>
                </div>
              </div>
            </template>

            <div
              v-else-if="chat && !chat.messages.length && !showWhatsApp"
              class="flex w-full h-full justify-center items-center text-gray-500"
            >
              {{ __('No history found for this conversation') }}
            </div>
            <div
              v-else-if="showWhatsApp && !whatsappMessages.length && activeWhatsAppChat"
              class="flex w-full h-full justify-center items-center text-gray-500"
            >
              {{ __('No messages found for this WhatsApp conversation') }}
            </div>
            <div v-else class="flex w-full h-full justify-center items-center text-gray-500">
              {{ __('Click on a conversation from left to see the histories.') }}
            </div>
          </div>

          <!-- Message Input -->
          <div v-if="chat && !showWhatsApp" class="flex-2 pt-4 pb-3">
            <div class="write bg-white shadow flex rounded-lg">
              <div class="flex-1">
                <textarea
                  v-model="message" name="message" class="w-full block outline-none text-sm py-4 px-4 bg-transparent"
                  rows="1" :placeholder="__('Type a message...')"
                  autofocus @keydown.enter.exact.prevent="sendMessage"
                />
              </div>
              <div class="flex-2 w-35 p-2 flex content-center items-center">
                <div class="flex-1">
                  <button
                    class="bg-blue-400 w-10 h-10 rounded-full flex justify-center items-center"
                    @click="sendMessage"
                  >
                    <icon class="w-4 h-4 fill-gray-100" name="send" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- WhatsApp Message Input -->
          <div v-if="showWhatsApp && activeWhatsAppChat" class="flex-2 pt-4 pb-3">
            <div class="write bg-white shadow flex rounded-lg">
              <div class="flex-1">
                <textarea
                  v-model="whatsappMessage" name="whatsappMessage"
                  class="w-full block outline-none text-sm py-4 px-4 bg-transparent"
                  rows="1" :placeholder="__('Type a WhatsApp message...')"
                  autofocus @keydown.enter.exact.prevent="sendWhatsAppMessage"
                />
              </div>
              <div class="flex-2 w-35 p-2 flex content-center items-center">
                <div class="flex-1">
                  <button
                    class="bg-green-500 w-10 h-10 rounded-full flex justify-center items-center"
                    @click="sendWhatsAppMessage"
                  >
                    <icon class="w-4 h-4 fill-gray-100" name="send" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- Lightbox Modal -->
  <div
    v-if="lightboxImage" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75"
    @click="lightboxImage = null"
  >
    <div class="relative max-w-4xl max-h-screen">
      <img :src="lightboxImage" class="max-w-full max-h-screen" alt="Enlarged view" />
      <button
        class="absolute top-4 right-4 p-2 bg-black bg-opacity-50 rounded-full text-white hover:bg-opacity-75"
      >
        <icon name="x" class="w-6 h-6" />
      </button>
    </div>
  </div>
</template>

<script>
import Icon from '@/Shared/Icon'
import pickBy from 'lodash/pickBy'
import Layout from '@/Shared/Layout'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import Pagination from '@/Shared/Pagination'
import SearchInput from '@/Shared/SearchInput'
import { Link, Head } from '@inertiajs/vue3'
import moment from 'moment'
import axios from 'axios'
import LoadingButton from '@/Shared/LoadingButton'
import { notify } from '@kyvg/vue3-notification'

export default {
    metaInfo: { title: 'Chat' },
    components: {
        SearchInput,
        Icon,
        Pagination,
        Link,
        Head,
        LoadingButton,
    },
    layout: Layout,
    props: {
        filters: Object,
        conversations: Object,
        chat: Object,
        title: String,
        whatsapp_conversations: {
            type: Array,
            default: () => [],
        },
    },
    remember: 'form',
    data() {
        return {
            statusPollInterval: null,
            message: null,
            whatsappMessage: null,
            user: this.$page.props.auth.user,
            user_access: this.$page.props.auth.user.access,
            form: {
                search: this.filters.search,
            },

            realTimeWhatsAppMessages: [],

            showWhatsApp: false,
            whatsappConversations: this.whatsapp_conversations || [],
            whatsappMessages: [],
            activeWhatsAppChat: null,
            refreshInterval: null,
            pollInterval: null,
            currentChatStatus: {
                isLive: true,
                customerName: '',
                lastSeen: null,
                lastRepliedBy: '',
                lastRepliedOn: null,
            },
            processingStatusChange: false,
            statusLoading: false,
            whatsappRealtimeSubscribed: false,
            lightboxImage: null,
            imageExtensions: ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'],
        }
    },
    watch: {
        form: {
            deep: true,
            handler: throttle(function () {
                this.$inertia.get(this.route('chat'), pickBy(this.form), { preserveState: true })
            }, 150),
        },
        activeWhatsAppChat(newVal) {
            if (newVal) {
                this.fetchChatStatus(newVal)
                this.startStatusPolling(newVal)
            } else if (this.statusPollInterval) {
                clearInterval(this.statusPollInterval)
            }
        },
    },
        created() {
        this.moment = moment

        // Initialize Echo for real-time updates
        if (window.Echo) {
            window.Echo.private('messages')
                .listen('NewChatMessage', (e) => {
                    const payload = e.message || e.chatMessage
                    if (typeof payload === 'object') {
                        this.pushMessage(payload)
                    }
                })
        }

        // Start polling
        this.startPolling()

        // Load WhatsApp conversations if needed
        if (this.whatsapp_conversations && this.whatsapp_conversations.length === 0) {
            this.loadWhatsAppConversations()
        }
    },
    beforeUnmount() {
        // Clear all intervals
        if (this.refreshInterval) {
            clearInterval(this.refreshInterval)
        }
        if (this.pollInterval) {
            clearInterval(this.pollInterval)
        }
        if (this.statusPollInterval) {
            clearInterval(this.statusPollInterval)
        }

        // Leave Echo channel
        if (window.Echo) {
            window.Echo.leave('messages')
        }
    },
    methods: {
        hasRegularChatMessage(message) {
            if (!this.chat || !Array.isArray(this.chat.messages)) {
                return false
            }

            return this.chat.messages.some((existing) => {
                if (existing.id && message.id) {
                    return existing.id === message.id
                }

                return (
                    existing.conversation_id === message.conversation_id &&
                    (existing.message || existing.content || '') === (message.message || message.content || '') &&
                    existing.user_id === message.user_id &&
                    existing.contact_id === message.contact_id &&
                    (existing.updated_at || existing.created_at) === (message.updated_at || message.created_at)
                )
            })
        },
        appendRegularChatMessage(message) {
            if (!message || !this.chat || this.hasRegularChatMessage(message)) {
                return
            }

            this.chat.messages.push(message)
        },
        hasWhatsAppMessage(message) {
            if (!Array.isArray(this.whatsappMessages)) {
                return false
            }

            return this.whatsappMessages.some((existing) => {
                if (existing.id && message.id) {
                    return existing.id === message.id
                }

                return (
                    existing.messageText === message.messageText &&
                    existing.fromPhone === message.fromPhone &&
                    existing.toPhone === message.toPhone &&
                    existing.sentTimeStamp === message.sentTimeStamp
                )
            })
        },
        appendWhatsAppMessage(message, { prepend = false } = {}) {
            if (!message || this.hasWhatsAppMessage(message)) {
                return
            }

            if (prepend) {
                this.whatsappMessages.unshift(message)
                return
            }

            this.whatsappMessages.push(message)
        },
        createOptimisticWhatsAppMessage(phone, text) {
            return {
                id: `temp-${Date.now()}`,
                messageText: text,
                fromPhone: this.user.email || '',
                toPhone: phone,
                sentTimeStamp: new Date().toISOString(),
                messageType: 'text',
                messageUrl: null,
                isRead: true,
                pending: true,
            }
        },
        replaceWhatsAppMessage(tempId, message) {
            const index = this.whatsappMessages.findIndex(existing => existing.id === tempId)

            if (index === -1) {
                this.appendWhatsAppMessage(message)
                return
            }

            this.whatsappMessages.splice(index, 1, message)
        },
        removeWhatsAppMessage(messageId) {
            this.whatsappMessages = this.whatsappMessages.filter(message => message.id !== messageId)
        },
        mergeWhatsAppMessages(serverMessages = []) {
            const pendingMessages = this.whatsappMessages.filter(message => message.pending)
            const mergedMessages = [...serverMessages]

            pendingMessages.forEach((pendingMessage) => {
                const alreadyPresent = mergedMessages.some((message) => {
                    if (message.id && pendingMessage.id) {
                        return message.id === pendingMessage.id
                    }

                    return (
                        message.messageText === pendingMessage.messageText &&
                        message.fromPhone === pendingMessage.fromPhone &&
                        message.toPhone === pendingMessage.toPhone &&
                        message.sentTimeStamp === pendingMessage.sentTimeStamp
                    )
                })

                if (!alreadyPresent) {
                    mergedMessages.push(pendingMessage)
                }
            })

            this.whatsappMessages = mergedMessages
        },
        // Add this method
        setupWhatsAppRealtime() {
            if (window.Echo && !this.whatsappRealtimeSubscribed) {
                this.whatsappRealtimeSubscribed = true
                window.Echo.private('whatsapp-messages')
                    .listen('NewWhatsAppMessage', (e) => {
                        if (
                            e.message &&
                            (
                            e.message.toPhone === this.activeWhatsAppChat ||
                            e.message.fromPhone === this.activeWhatsAppChat
                            )
                        ) {
                            this.appendWhatsAppMessage(e.message, { prepend: true })
                        }
                    })
            }
        },
        // Status related methods
        async fetchChatStatus(phoneNumber) {
            if (!phoneNumber || !this.showWhatsApp) return

            try {
                this.statusLoading = true
                const response = await axios.get(
                    this.route('chat.whatsapp.get-status', { phone: phoneNumber }),
                )

                if (response.data.success) {
                    this.currentChatStatus = {
                        isLive: response.data.isLive,
                        customerName: response.data.customerName || '',
                        lastSeen: response.data.lastSeen || null,
                        lastRepliedBy: response.data.lastRepliedBy || '',
                        lastRepliedOn: response.data.lastRepliedOn || null,
                    }

                    const conversation = this.whatsappConversations.find(c => c.phone === phoneNumber)
                    if (conversation) {
                        conversation.isLive = response.data.isLive
                        conversation.last_available_on = response.data.lastSeen
                    }
                }
            } catch (error) {
                console.error('Error fetching chat status:', error)
            } finally {
                this.statusLoading = false
            }
        },

        async startStatusPolling(phoneNumber) {
            if (!phoneNumber || !this.showWhatsApp) return

            // Clear any existing interval
            if (this.statusPollInterval) {
                clearInterval(this.statusPollInterval)
            }

            // Start a new polling interval
            this.statusPollInterval = setInterval(() => {
                this.fetchChatStatus(phoneNumber)
            }, 1500) // Poll every 1.5 seconds
            this.fetchChatStatus(phoneNumber) // Initial fetch
        },

        async toggleLiveChatStatus() {
            if (!this.activeWhatsAppChat || this.processingStatusChange) return

            try {
                this.processingStatusChange = true
                const newStatus = !this.currentChatStatus.isLive

                // Show loading notification
                const loadingNotification = notify({
                    title: 'Updating Status',
                    text: newStatus ? 'Starting live chat...' : 'Ending live chat...',
                    type: 'info',
                    duration: 0,
                })

                // Call your Laravel endpoint instead of direct API call
                const response = await axios.post(
                    this.route('chat.whatsapp.update-status'),
                    {
                        phone: this.activeWhatsAppChat,
                        isLive: newStatus,
                        adminName: `${this.user.first_name} ${this.user.last_name}`,
                    },
                )

                // Remove loading notification
                loadingNotification.close()

                if (response.data.success) {
                    this.currentChatStatus.isLive = response.data.isLive
                    const conversation = this.whatsappConversations.find(c => c.phone === this.activeWhatsAppChat)
                    if (conversation) {
                        conversation.isLive = response.data.isLive
                        conversation.last_available_on = response.data.updatedAt
                    }

                    notify({
                        title: 'Success',
                        text: newStatus
                            ? `Live chat started with ${this.activeWhatsAppChat}`
                            : `Live chat ended with ${this.activeWhatsAppChat}`,
                        type: 'success',
                        duration: 5000,
                    })
                } else {
                    notify({
                        title: 'Error',
                        text: response.data.message || 'Failed to update status',
                        type: 'error',
                        duration: 5000,
                    })
                }
            } catch (error) {
                console.error('Error toggling chat status:', error)
                notify({
                    title: 'Error',
                    text: error.response?.data?.message || 'Failed to update status',
                    type: 'error',
                    duration: 5000,
                })
            } finally {
                this.processingStatusChange = false
            }
        },

        // WhatsApp methods
        async loadWhatsAppConversations() {
            try {
                const response = await axios.get(this.route('chat.whatsapp.conversations'))
                this.whatsappConversations = response.data.map(conv => ({
                    ...conv,
                    isLive: false,
                    displayDate: conv.last_available_on,
                }))

                await Promise.all(this.whatsappConversations.map(async (conv) => {
                    try {
                        const statusResponse = await axios.get(
                            `${this.$page.props.urls.chat_status_base}/api/chat/status/customer/${conv.phone}`,
                            { validateStatus: () => true },
                        )
                        if (statusResponse.status === 200) {
                            conv.isLive = statusResponse.data.isLive
                        }
                    } catch (error) {
                        console.error(`Error checking status for ${conv.phone}:`, error)
                    }
                }))

                if (this.activeWhatsAppChat) {
                    await this.fetchChatStatus(this.activeWhatsAppChat)
                }
            } catch (error) {
                console.error('Error loading WhatsApp conversations:', error)
            }
        },
        // In your loadWhatsAppChat method:
        async loadWhatsAppChat(phone) {
            this.activeWhatsAppChat = phone
            try {
                const response = await axios.get(this.route('chat.whatsapp.messages', { phone }))
                this.mergeWhatsAppMessages(response.data)

                const conversation = this.whatsappConversations.find(c => c.phone === phone)
                if (conversation) {
                    conversation.unread_count = 0
                }

                this.setupWhatsAppRealtime()
                await this.fetchChatStatus(phone)
                this.startStatusPolling(phone)
            } catch (error) {
                console.error('Error loading WhatsApp chat:', error)
            }
        },

        toggleWhatsAppView() {
            this.showWhatsApp = !this.showWhatsApp
            if (this.showWhatsApp) {
                this.loadWhatsAppConversations()
                // Load messages for the first WhatsApp chat if available
                if (this.whatsappConversations.length > 0) {
                    this.loadWhatsAppChat(this.whatsappConversations[0].phone)
                }
                // Refresh conversations and messages more frequently
                this.refreshInterval = setInterval(() => {
                    this.loadWhatsAppConversations()
                    if (this.activeWhatsAppChat) {
                        this.loadWhatsAppChat(this.activeWhatsAppChat)
                    }
                }, 1500) // Refresh every 1.5 seconds
                // Setup realtime listener for new WhatsApp messages
                this.setupWhatsAppRealtime()
            } else {
                if (this.refreshInterval) {
                    clearInterval(this.refreshInterval)
                    this.refreshInterval = null
                }
                if (this.statusPollInterval) {
                    clearInterval(this.statusPollInterval)
                    this.statusPollInterval = null
                }
                this.activeWhatsAppChat = null
                this.whatsappMessages = []
            }
        },

        // Regular chat methods
        sendMessage() {
            const that = this
            const messageData = {
                message: this.message,
                user_id: this.user.id,
                _token: this.$page.props.csrf_token,
                conversation_id: this.chat.id,
            }
            this.message = ''
            axios.post(this.route('chat.message'), messageData)
                .then((response) => {
                    if (response.data) {
                        that.appendRegularChatMessage(response.data)
                        this.pollForChatUpdates()
                    }
                })
                .catch((error) => {
                    console.error('Error sending message:', error)
                })
        },
        formatWhatsAppConversationDate(conversation) {
            const value = conversation.displayDate || conversation.last_available_on

            if (!value) {
                return ''
            }

            return moment(value).format('DD MMM')
        },

        sendWhatsAppMessage() {
            if (!this.activeWhatsAppChat || !this.whatsappMessage) return

            const messageText = this.whatsappMessage
            const messageData = {
                To: this.activeWhatsAppChat,
                Message: messageText,
                MessageType: 'text',
                _token: this.$page.props.csrf_token,
            }
            const optimisticMessage = this.createOptimisticWhatsAppMessage(
                this.activeWhatsAppChat,
                messageText,
            )

            this.appendWhatsAppMessage(optimisticMessage)

            this.whatsappMessage = ''

            axios.post(this.route('chat.whatsapp.send'), messageData)
                .then(response => {
                    if (response.data.success) {
                        if (response.data.stored_message) {
                            this.replaceWhatsAppMessage(
                                optimisticMessage.id,
                                response.data.stored_message,
                            )
                        } else {
                            this.removeWhatsAppMessage(optimisticMessage.id)
                            this.loadWhatsAppChat(this.activeWhatsAppChat)
                        }
                    } else {
                        this.removeWhatsAppMessage(optimisticMessage.id)
                        this.whatsappMessage = messageText
                    }
                })
                .catch(error => {
                    this.removeWhatsAppMessage(optimisticMessage.id)
                    this.whatsappMessage = messageText
                    console.error('Error sending WhatsApp message:', error)
                })
        },

        // Other methods
        navigateTo(id) {
            window.location.href = this.route('chat.current', id)
        },

        destroy(id) {
            if (confirm('Are you sure you want to delete this conversation?')) {
                this.$inertia.delete(this.route('chat.destroy', id), {
                    onSuccess: () => {
                        setTimeout(() => this.reset(), 4000)
                    },
                })
            }
        },

        restore(id) {
            if (confirm('Are you sure you want to restore this conversation?')) {
                this.$inertia.put(this.route('chat.restore', id))
            }
        },

        pushMessage(message) {
            const chat = this.conversations.data.find(x => x.id === message.conversation_id)
            const content = message.message || message.content || ''
            const isIncoming = Boolean(message.contact_id || message.contact)

            if (typeof chat === 'object') {
                chat.title = content
                chat.total_entry = isIncoming ? (chat.total_entry + 1) : chat.total_entry

                if (this.chat && (this.chat.id === message.conversation_id)) {
                    this.appendRegularChatMessage(message)
                    chat.total_entry = 0
                }
            } else {
                this.conversations.data.unshift({
                    'id': message.conversation_id,
                    'total_entry': isIncoming ? 1 : 0,
                    'title': content,
                    'creator': message.creator || message.contact,
                    'updated_at': message.updated_at,
                })
            }
        },

        startPolling() {
            if (this.pollInterval) {
                clearInterval(this.pollInterval)
            }

            this.pollInterval = setInterval(() => {
                this.pollForChatUpdates()
            }, 1500) // Poll every 1.5 seconds
        },

        async pollForChatUpdates() {
            try {
                if (this.chat) {
                    const response = await axios.get(
                        this.route('chat.current.ajax', this.chat.id),
                    )

                    if (response.data.messages.length !== this.chat.messages.length) {
                        this.chat.messages = response.data.messages
                    }

                    await this.refreshConversationList()
                } else {
                    await this.refreshConversationList()
                }
            } catch (error) {
                console.error('Error polling for chat updates:', error)
            }
        },

        async refreshConversationList() {
            try {
                const response = await axios.get(
                    this.route('chat.conversations.ajax'),
                )
                this.conversations.data = response.data
            } catch (error) {
                console.error('Error refreshing conversation list:', error)
            }
        },

        //Imaging Download Methods
        isImageUrl(text) {
            if (!text) return false
            try {
                const url = new URL(text)
                const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg']
                return imageExtensions.some(ext =>
                    url.pathname.toLowerCase().endsWith(ext),
                )
            } catch {
                return false
            }
        },

        isFileUrl(text) {
            if (!text) return false
            try {
                new URL(text)
                return text.startsWith('http') && !this.isImageUrl(text)
            } catch {
                return false
            }
        },

        getFileNameFromUrl(url) {
            try {
                const parsed = new URL(url)
                const pathParts = parsed.pathname.split('/')
                return pathParts[pathParts.length - 1] || 'File'
            } catch {
                return 'File'
            }
        },

        openLightbox(imageUrl) {
            this.lightboxImage = imageUrl
        },

        async downloadFile(url, type = 'file') {
            try {
                // For WhatsApp files, we might need to handle authentication
                const response = await axios.get(this.route('chat.whatsapp.download'), {
                    params: { url },
                    responseType: 'blob',
                })

                // Create a blob URL for the file
                const blobUrl = window.URL.createObjectURL(new Blob([response.data]))

                // Create a temporary anchor element to trigger download
                const link = document.createElement('a')
                link.href = blobUrl

                // Extract filename from URL or use a default
                const filename = this.getFileNameFromUrl(url) ||
                    `whatsapp_${type}_${new Date().getTime()}.${type === 'image' ? 'jpg' : 'bin'}`

                link.setAttribute('download', filename)
                document.body.appendChild(link)
                link.click()

                // Clean up
                link.remove()
                window.URL.revokeObjectURL(blobUrl)

            } catch (error) {
                console.error('Error downloading file:', error)
                notify({
                    title: 'Error',
                    text: 'Failed to download file',
                    type: 'error',
                })
            }
        },

        reset() {
            this.form = mapValues(this.form, () => null)
        },
    },
}
</script>

<style scoped>
.user_icon {
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #e2e8f0;
    color: #4a5568;
    font-weight: bold;
}

.bg-green-100 {
    background-color: #dcf8c6;
}

.bg-green-600 {
    background-color: #25D366;
}

.bg-green-500 {
    background-color: #128C7E;
}

.status-indicator {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    display: inline-block;
    margin-right: 5px;
}

.status-active {
    background-color: #25D366;
}

.status-inactive {
    background-color: #9CA3AF;
}

.loading-spinner {
    animation: spin 1s linear infinite;
}

.max-w-xs {
    max-width: 20rem;
}

.max-h-48 {
    max-height: 12rem;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}
</style>
